import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const DEFAULT = { number: "", title: "", section: "", content: "", order: 0 };

export default function AdminRegulation() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        const { data } = await api.get("/regulation");
        setItems(data);
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const save = async (a) => {
        setErr("");
        try {
            if (a.id) await api.put(`/regulation/${a.id}`, a);
            else await api.post("/regulation", a);
            setEditing(null);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questo articolo?")) return;
        try {
            await api.delete(`/regulation/${id}`);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div data-testid="admin-regulation">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="op-heading text-3xl">Regolamento</h2>
                    <p className="op-label mt-2">{items.length} articoli</p>
                </div>
                <button
                    className="op-btn op-btn-primary"
                    onClick={() => setEditing({ ...DEFAULT, order: items.length })}
                    data-testid="article-add-btn"
                >
                    + Nuovo articolo
                </button>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-4">
                    {err}
                </div>
            )}

            <div className="space-y-2">
                {items.map((a) => (
                    <div
                        key={a.id}
                        className="op-card p-4 flex items-center justify-between gap-4"
                        data-testid={`article-row-${a.number}`}
                    >
                        <div className="min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                                <span className="op-mono text-t2bb6ff text-xs border border-t2bb6ff/40 px-1.5 py-0.5">
                                    ART. {a.number}
                                </span>
                                <span className="op-label">{a.section || "—"}</span>
                            </div>
                            <div className="text-tffffff font-medium truncate">{a.title}</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button
                                onClick={() => setEditing(a)}
                                className="op-btn py-1.5 px-3 text-xs"
                                data-testid={`article-edit-btn-${a.number}`}
                            >
                                Modifica
                            </button>
                            <button
                                onClick={() => remove(a.id)}
                                className="op-btn op-btn-ghost py-1.5 px-3 text-xs text-tff3b30 hover:text-tff3b30"
                                data-testid={`article-delete-btn-${a.number}`}
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <ArticleEditor value={editing} onCancel={() => setEditing(null)} onSave={save} />
            )}
        </div>
    );
}

function ArticleEditor({ value, onCancel, onSave }) {
    const [a, setA] = useState(value);
    const [tab, setTab] = useState("edit");
    const upd = (k, v) => setA((p) => ({ ...p, [k]: v }));
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="article-editor">
            <div className="op-card w-full max-w-4xl p-6 my-auto">
                <div className="op-label text-t2bb6ff">
                    {a.id ? "// MODIFICA ARTICOLO" : "// NUOVO ARTICOLO"}
                </div>
                <h3 className="op-heading text-2xl mt-2">Articolo {a.number || "—"}</h3>
                <div className="mt-5 grid grid-cols-3 gap-3">
                    <div>
                        <label className="op-label block mb-2">Numero</label>
                        <input
                            value={a.number}
                            onChange={(e) => upd("number", e.target.value)}
                            className="op-input op-mono"
                            placeholder="01"
                            data-testid="article-number-input"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="op-label block mb-2">Titolo</label>
                        <input
                            value={a.title}
                            onChange={(e) => upd("title", e.target.value)}
                            className="op-input"
                            data-testid="article-title-input"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-2">Sezione</label>
                        <input
                            value={a.section || ""}
                            onChange={(e) => upd("section", e.target.value)}
                            className="op-input"
                            placeholder="Match · Mercato · Economia…"
                            data-testid="article-section-input"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-2">Ordine</label>
                        <input
                            type="number"
                            value={a.order || 0}
                            onChange={(e) => upd("order", parseInt(e.target.value) || 0)}
                            className="op-input op-mono"
                            data-testid="article-order-input"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2 mt-6 border-b border-t22252a">
                    <button
                        onClick={() => setTab("edit")}
                        className={`op-label px-3 py-2 border-b-2 ${
                            tab === "edit" ? "border-t2bb6ff text-tffffff" : "border-transparent"
                        }`}
                        data-testid="article-tab-edit"
                    >
                        Editor (Markdown)
                    </button>
                    <button
                        onClick={() => setTab("preview")}
                        className={`op-label px-3 py-2 border-b-2 ${
                            tab === "preview" ? "border-t2bb6ff text-tffffff" : "border-transparent"
                        }`}
                        data-testid="article-tab-preview"
                    >
                        Anteprima
                    </button>
                </div>

                {tab === "edit" ? (
                    <textarea
                        value={a.content}
                        onChange={(e) => upd("content", e.target.value)}
                        className="op-input op-mono mt-4 min-h-[300px] text-sm"
                        placeholder="Contenuto in Markdown… supporta **grassetto**, tabelle, liste, ecc."
                        data-testid="article-content-input"
                    />
                ) : (
                    <div className="op-prose mt-4 max-h-[400px] overflow-y-auto pr-4 border border-t22252a p-4">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.content || "_Vuoto_"}</ReactMarkdown>
                    </div>
                )}

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onCancel} className="op-btn" data-testid="article-cancel-btn">
                        Annulla
                    </button>
                    <button onClick={() => onSave(a)} className="op-btn op-btn-primary" data-testid="article-save-btn">
                        Salva
                    </button>
                </div>
            </div>
        </div>
    );
}
